import {Box, Heading, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {all, always, any, filter, length, pipe, propEq, values} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {buildArray, EMPTY_PLACEHOLDER, Nullish} from 'shared/web';

import {Alert} from '../../components/Alert/Alert';
import {CarouselPrint} from '../../components/Carousel/Carousel.print';
import {Comment} from '../../components/Comment/CommentExpandable';
import {ListFeaturesItemProps} from '../../components/ListFeatures/components/ListFeaturesItem';
import {ListFeatures} from '../../components/ListFeatures/ListFeatures';
import {SectionPrint} from '../../components/SectionPrint/SectionPrint';
import {SectionStateFlagProps} from '../../components/SectionStateFlag/SectionStateFlag';
import {getSlideshowByUniqueKey} from '../../components/Slideshow/utils/getSlideshowByUniqueKey';
import {useGetDigitalCertificateData} from '../../hooks/useGetDigitalCertificateData';
import {useLocale} from '../../hooks/useLocale';
import i18n from '../../i18n/i18n';
import {filterListFeatures} from '../../utils/filterListFeatures';
import {getMechanicFeatures} from '../../utils/getMechanicFeatures';
import {getMechanics} from '../../utils/getMechanics';
import {getRecordsTranslate} from '../../utils/getRecordsTranslate';
import {getValueWithUnit} from '../../utils/getValueWithUnit';
import {isB2cCarAuditWorkspace} from '../../utils/isB2cCarAuditWorkspace';
import {getBrakeLiningWearSeverity} from './utils/getBrakeLiningWearSeverity';
import {hasBrakeDiscInfo} from './utils/hasBrakeDiscInfo';
import {hasBrakeLiningWearInfo} from './utils/hasBrakeLiningWearInfo';
import {hasBrakePadInfo} from './utils/hasBrakePadInfo';

interface MechanicsPrintProps {
  showEmptyFields: boolean;
}

export function MechanicsPrint(props: MechanicsPrintProps) {
  const locale = useLocale();

  const {vehicleAudit} = useGetDigitalCertificateData();

  const mechanics = getMechanics({vehicleAudit, language: locale.language});
  const mechanicSlideshow = getSlideshowByUniqueKey({
    vehicleAudit,
    language: locale.language,
    parentUniqueKey: 'MECHANICS',
    isSlidesRequired: true,
  });

  const mechanicFeatures = getMechanicFeatures(mechanics, props.showEmptyFields);

  if (!mechanicFeatures || !mechanics) {
    return null;
  }

  const {
    brakeSystemFeatures,
    driveSystemFeatures,
    chassisFeatures,
    electricalSystemFeatures,
    diagnosticsFeatures,
    bodyConditionFeatures,
  } = mechanicFeatures.features;

  const features = mechanicFeatures.allFeatures;
  const isNotTested = all((f) => f.variant === 'neutral', features);
  const isDamaged = any((f) => f.variant === 'damaged', features);
  const countDamages: (object: object) => number = pipe(
    values,
    filter(propEq('damaged', 'variant')),
    length
  );

  const breakPad = {
    lf: {
      valueWithUnit: getValueWithUnit({
        auditValue: {
          value: mechanics.brake.brakePad.LF.value,
          unit: mechanics.brake.brakePad.LF.unit,
        },
      }),
      severity: getBrakeLiningWearSeverity(mechanics.brake.brakePad.LF.value),
    },
    rf: {
      valueWithUnit: getValueWithUnit({
        auditValue: {
          value: mechanics.brake.brakePad.RF.value,
          unit: mechanics.brake.brakePad.RF.unit,
        },
      }),
      severity: getBrakeLiningWearSeverity(mechanics.brake.brakePad.RF.value),
    },
    lr: {
      valueWithUnit: getValueWithUnit({
        auditValue: {
          value: mechanics.brake.brakePad.LR.value,
          unit: mechanics.brake.brakePad.LR.unit,
        },
      }),
      severity: getBrakeLiningWearSeverity(mechanics.brake.brakePad.LR.value),
    },
    rr: {
      valueWithUnit: getValueWithUnit({
        auditValue: {
          value: mechanics.brake.brakePad.RR.value,
          unit: mechanics.brake.brakePad.RR.unit,
        },
      }),
      severity: getBrakeLiningWearSeverity(mechanics.brake.brakePad.RR.value),
    },
  };

  const brakeLiningWear = {
    lf: {
      valueWithUnit: getValueWithUnit({
        auditValue: {
          value: mechanics.brake.brakeLiningWear.LF.value,
          unit: mechanics.brake.brakeLiningWear.LF.unit,
        },
      }),
      severity: 'neutral',
    },
    rf: {
      valueWithUnit: getValueWithUnit({
        auditValue: {
          value: mechanics.brake.brakeLiningWear.RF.value,
          unit: mechanics.brake.brakeLiningWear.RF.unit,
        },
      }),
      severity: 'neutral',
    },
    lr: {
      valueWithUnit: getValueWithUnit({
        auditValue: {
          value: mechanics.brake.brakeLiningWear.LR.value,
          unit: mechanics.brake.brakeLiningWear.LR.unit,
        },
      }),
      severity: 'neutral',
    },
    rr: {
      valueWithUnit: getValueWithUnit({
        auditValue: {
          value: mechanics.brake.brakeLiningWear.RR.value,
          unit: mechanics.brake.brakeLiningWear.RR.unit,
        },
      }),
      severity: 'neutral',
    },
  } as const;

  return (
    <SectionPrint
      icon="mechanics"
      heading={i18n.t('mechanicsHeader')}
      flag={match<[boolean, boolean], Nullish | SectionStateFlagProps>([isNotTested, isDamaged])
        .with(
          [false, true],
          always({severity: 'damage', text: getRecordsTranslate(countDamages(features))})
        )
        .with([true, false], always(null))
        .otherwise(always({severity: 'good', text: i18n.t('sectionState.good')}))}
    >
      <VStack spacing={6}>
        <Show when={props.showEmptyFields || isNotNilOrEmpty(brakeSystemFeatures)}>
          <Heading size={2}>{i18n.t('brakeSystem')}</Heading>
          <ListFeatures columns={2} spacing={3} features={brakeSystemFeatures} />
        </Show>
        <Show when={hasBrakeDiscInfo(mechanics.brake, props.showEmptyFields)}>
          <Heading size={2}>{i18n.t('brakeDisc')}</Heading>
          <ListFeatures
            columns={2}
            spacing={3}
            features={filterListFeatures(
              [
                {
                  title: i18n.t('valueLF'),
                  variant: mechanics.brake.brakeDiscLF.variant,
                  flagTexts: mechanics.brake.brakeDiscLF.flagTexts,
                  description: mechanics.brake.brakeDiscLF.description,
                },
                {
                  title: i18n.t('valueRF'),
                  variant: mechanics.brake.brakeDiscRF.variant,
                  flagTexts: mechanics.brake.brakeDiscRF.flagTexts,
                  description: mechanics.brake.brakeDiscRF.description,
                },
                {
                  title: i18n.t('valueLR'),
                  variant: mechanics.brake.brakeDiscLR.variant,
                  flagTexts: mechanics.brake.brakeDiscLR.flagTexts,
                  description: mechanics.brake.brakeDiscLR.description,
                },
                {
                  title: i18n.t('valueRR'),
                  variant: mechanics.brake.brakeDiscRR.variant,
                  flagTexts: mechanics.brake.brakeDiscRR.flagTexts,
                  description: mechanics.brake.brakeDiscRR.description,
                },
              ],
              props.showEmptyFields
            )}
          />
        </Show>
        <Show when={hasBrakePadInfo(mechanics.brake.brakePad, props.showEmptyFields)}>
          <Heading size={2}>{i18n.t('brakePad')}</Heading>
          <ListFeatures
            columns={2}
            spacing={3}
            features={buildArray<ListFeaturesItemProps>()
              .when(breakPad.lf.valueWithUnit || props.showEmptyFields, {
                title: i18n.t('valueLF'),
                value: breakPad.lf.valueWithUnit ?? EMPTY_PLACEHOLDER,
                variant: breakPad.lf.severity,
              })
              .when(breakPad.rf.valueWithUnit || props.showEmptyFields, {
                title: i18n.t('valueRF'),
                value: breakPad.rf.valueWithUnit ?? EMPTY_PLACEHOLDER,
                variant: breakPad.rf.severity,
              })
              .when(breakPad.lr.valueWithUnit || props.showEmptyFields, {
                title: i18n.t('valueLR'),
                value: breakPad.lr.valueWithUnit ?? EMPTY_PLACEHOLDER,
                variant: breakPad.lr.severity,
              })
              .when(breakPad.rr.valueWithUnit || props.showEmptyFields, {
                title: i18n.t('valueRR'),
                value: breakPad.rr.valueWithUnit ?? EMPTY_PLACEHOLDER,
                variant: breakPad.rr.severity,
              })}
          />
          <Show when={isNotNilOrEmpty(mechanics.brake.brakePad.comment)}>
            <Box maxWidth={250}>
              <Comment title={i18n.t('note')} comment={mechanics.brake.brakePad.comment} />
            </Box>
          </Show>
        </Show>
        <Show when={hasBrakeLiningWearInfo(mechanics.brake.brakeLiningWear, props.showEmptyFields)}>
          <Heading size={2}>{i18n.t('brakeLiningWear')}</Heading>
          <Show when={isB2cCarAuditWorkspace()}>
            <Box width="fit-content">
              <Alert>{i18n.t('brakeLiningWearValuesAlert')}</Alert>
            </Box>
          </Show>
          <ListFeatures
            columns={2}
            spacing={3}
            features={buildArray<ListFeaturesItemProps>()
              .when(brakeLiningWear.lf.valueWithUnit || props.showEmptyFields, {
                title: i18n.t('valueLF'),
                value: brakeLiningWear.lf.valueWithUnit ?? EMPTY_PLACEHOLDER,
                variant: brakeLiningWear.lf.severity,
              })
              .when(brakeLiningWear.rf.valueWithUnit || props.showEmptyFields, {
                title: i18n.t('valueRF'),
                value: brakeLiningWear.rf.valueWithUnit ?? EMPTY_PLACEHOLDER,
                variant: brakeLiningWear.rf.severity,
              })
              .when(brakeLiningWear.lr.valueWithUnit || props.showEmptyFields, {
                title: i18n.t('valueLR'),
                value: brakeLiningWear.lr.valueWithUnit ?? EMPTY_PLACEHOLDER,
                variant: brakeLiningWear.lr.severity,
              })
              .when(brakeLiningWear.rr.valueWithUnit || props.showEmptyFields, {
                title: i18n.t('valueRR'),
                value: brakeLiningWear.rr.valueWithUnit ?? EMPTY_PLACEHOLDER,
                variant: brakeLiningWear.rr.severity,
              })}
          />
          <Show when={isNotNilOrEmpty(mechanics.brake.brakeLiningWear.comment)}>
            <Box maxWidth={250}>
              <Comment title={i18n.t('note')} comment={mechanics.brake.brakeLiningWear.comment} />
            </Box>
          </Show>
        </Show>
        <Show when={props.showEmptyFields || isNotNilOrEmpty(driveSystemFeatures)}>
          <Heading size={2}>{i18n.t('driveSystem')}</Heading>
          <ListFeatures columns={2} spacing={3} features={driveSystemFeatures} />
        </Show>

        <Show when={props.showEmptyFields || isNotNilOrEmpty(chassisFeatures)}>
          <Heading size={2}>{i18n.t('chassis')}</Heading>
          <ListFeatures columns={[1, 2, 2, 2]} spacing={[3, 6, 6, 10]} features={chassisFeatures} />
        </Show>
        <Show when={props.showEmptyFields || isNotNilOrEmpty(electricalSystemFeatures)}>
          <Heading size={2}>{i18n.t('electricalSystem')}</Heading>
          <ListFeatures columns={2} spacing={3} features={electricalSystemFeatures} />
        </Show>
        <Show when={props.showEmptyFields || isNotNilOrEmpty(diagnosticsFeatures)}>
          <Heading size={2}>{i18n.t('diagnostics')}</Heading>
          <ListFeatures columns={2} spacing={3} features={diagnosticsFeatures} />
        </Show>
        <Show when={props.showEmptyFields || isNotNilOrEmpty(bodyConditionFeatures)}>
          <Heading size={2}>{i18n.t('bodyCondition')}</Heading>
          <ListFeatures columns={2} spacing={3} features={bodyConditionFeatures} />
        </Show>
        <Show when={isNotNilOrEmpty(mechanicSlideshow)}>
          <CarouselPrint data={mechanicSlideshow} />
        </Show>
      </VStack>
    </SectionPrint>
  );
}
